import React from 'react';
import PreviewShell from './PreviewShell';

// ─── pixel-accurate layout-mode previews ─────────────────────────────────────
// CARD_HEIGHT = 421px. Header ≈ 44px. Body ≈ 377px.
// PreviewShell adds padding: 8px 12px 12px  →  usable ≈ 357px tall.
// Every body is React.memo (very low re-renders during drag).
// Colors taken directly from real card SCSS/component files.

// ─── SVG full pie (solid wedge paths, innerRadius=0, matching ResponsivePie) ──
const PieChart = ({ segments, size }) => {
	const cx = size / 2;
	const cy = size / 2;
	const r = size / 2 - 1;
	let angle = -Math.PI / 2;
	return (
		<svg width={size} height={size} style={{ flexShrink: 0 }}>
			{segments.map(({ pct, color, label }, i) => {
				const sweep = (pct / 100) * 2 * Math.PI;
				const end = angle + sweep;
				const x1 = cx + r * Math.cos(angle);
				const y1 = cy + r * Math.sin(angle);
				const x2 = cx + r * Math.cos(end);
				const y2 = cy + r * Math.sin(end);
				const mid = angle + sweep / 2;
				const lx = cx + r * 0.62 * Math.cos(mid);
				const ly = cy + r * 0.62 * Math.sin(mid);
				const d = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${sweep > Math.PI ? 1 : 0},1 ${x2},${y2} Z`;
				angle = end;
				return (
					<g key={i}>
						<path d={d} fill={color} />
						{label && (
							<text x={lx} y={ly} textAnchor="middle" dominantBaseline="central"
								fill="#fff" fontSize={size > 160 ? 11 : 9} fontWeight="700" fontFamily="manrope">{label}</text>
						)}
					</g>
				);
			})}
		</svg>
	);
};

// ─── SVG radial bar (concentric arc rings, matching ResponsiveRadialBar) ──────
const RadialBarChart = ({ entries, size }) => {
	const cx = size / 2;
	const cy = size / 2;
	const outerR = size / 2 - 2;
	const ringSpacing = outerR / entries.length;
	const ringWidth = ringSpacing * 0.6;
	return (
		<svg width={size} height={size} style={{ flexShrink: 0 }}>
			{entries.map(({ pct, color }, i) => {
				const rMid = outerR - i * ringSpacing - ringWidth / 2;
				const circ = 2 * Math.PI * rMid;
				const dash = (pct / 100) * circ;
				return (
					<circle key={i} cx={cx} cy={cy} r={rMid}
						fill="none" stroke={color} strokeWidth={ringWidth}
						strokeDasharray={`${dash} ${circ - dash}`}
						strokeDashoffset={circ * 0.25}
						strokeLinecap="round"
						transform={`rotate(-90 ${cx} ${cy})`}
					/>
				);
			})}
		</svg>
	);
};

// ─── HospitalizationsList ─────────────────────────────────────────────────────
// Real: border 1px solid #E8E8EB radius 8px, label 12px #636578 manrope-med,
//       progress bar bg #EEF3F7 fill #92C7FF h 10px, badge bg #4879F5 white.
// Two items fill ~357px with spacious padding to match real 20px padding.

export const HospitalizationsPreviewBody = React.memo(({ useGenericLabels = false }) => (
	<PreviewShell>
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '12px 10px', height: '100%', boxSizing: 'border-box', justifyContent: 'center' }}>
			{(useGenericLabels
				? [
					{ label: 'Test 1', count: 8, pct: 80, color: '#82C91E', badgeColor: '#00B8E6', progressColor: '#00C2FF' },
					{ label: 'Test 2', count: 2, pct: 20, color: '#0047FF', badgeColor: '#4879F5', progressColor: '#4879F5' },
				]
				: [
					{ label: 'New Hospitalizations', count: 8, pct: 80, color: '#82C91E', badgeColor: '#00B8E6', progressColor: '#00C2FF' },
					{ label: 'Re-Hospitalizations', count: 2, pct: 20, color: '#0047FF', badgeColor: '#4879F5', progressColor: '#4879F5' },
				]).map(({ label, count, pct, color, badgeColor, progressColor }) => (
					<div key={label} style={{
						border: '1px solid #E8E8EB', borderRadius: 12,
						padding: '16px',
						display: 'flex', flexDirection: 'column', gap: 12,
						backgroundColor: '#fff', position: 'relative'
					}}>
						{/* Header: Title + Checkbox */}
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<span style={{ fontFamily: 'manrope', fontWeight: 600, fontSize: 13, color: '#636578' }}>{label}</span>
							<div style={{ width: 14, height: 14, border: '1.5px solid #E2E8F0', borderRadius: 4 }} />
						</div>

						{/* Data Row: Indicator + Badge + Progress */}
						<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
							<div style={{ width: 6, height: 20, backgroundColor: color, borderRadius: 3, flexShrink: 0 }} />

							{/* Value Badge */}
							<div style={{
								minWidth: 80, height: 32, borderRadius: 8, backgroundColor: badgeColor,
								display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
								boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
							}}>
								<span style={{ fontFamily: 'manrope', fontSize: 11, fontWeight: 700, color: '#fff' }}>
									{count} ({pct}%)
								</span>
							</div>

							{/* Progress Bar */}
							<div style={{ flex: 1, height: 10, backgroundColor: '#DDE7FF', borderRadius: 10, overflow: 'hidden' }}>
								<div style={{ width: `${pct}%`, height: '100%', backgroundColor: progressColor, borderRadius: 10 }} />
							</div>
						</div>
					</div>
				))}
		</div>
	</PreviewShell>
));
HospitalizationsPreviewBody.displayName = 'HospitalizationsPreviewBody';

// ─── DischargeEmergencyList (DC / ER) ─────────────────────────────────────────
// Left 46%: DC bar #606BDF, ER bar #87BCFE, bar wrapper 50px wide 181.5px tall.
// Right 54%: pie chart colors #606BDF + #87BCFE.

export const DcerPreviewBody = React.memo(() => (
	<PreviewShell>
		<div style={{ display: 'flex', alignItems: 'flex-end', width: '100%', height: '100%', paddingBottom: 8 }}>
			{/* Left 46% — vertical bars */}
			<div style={{ width: '46%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', borderRight: '1px solid #f1f1f4', height: '100%', gap: 20, paddingBottom: 8 }}>
				{[
					{ key: 'DC', pct: 62, color: '#606BDF', h: 0.60 },
					{ key: 'ER', pct: 38, color: '#87BCFE', h: 0.38 },
				].map(({ key, pct, color, h }) => (
					<div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 6, height: '100%' }}>
						<span style={{ fontFamily: 'manrope', fontWeight: 700, fontSize: 14, color, lineHeight: 1 }}>
							{key === 'DC' ? '5' : '3'}
						</span>
						<div style={{ width: 48, height: `${Math.round(h * 100)}%`, backgroundColor: color, borderRadius: '5px 5px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 30 }}>
							<span style={{ fontSize: 11, fontWeight: 700, color: '#fff', writingMode: 'horizontal-tb' }}>{pct}%</span>
						</div>
						<span style={{ fontFamily: 'manrope', fontWeight: 700, fontSize: 14, color: '#444652' }}>{key}</span>
					</div>
				))}
			</div>
			{/* Right 54% — full solid pie */}
			<div style={{ width: '54%', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
				<PieChart size={160} segments={[
					{ pct: 62, color: '#606BDF', label: '5 (62%)' },
					{ pct: 38, color: '#87BCFE', label: '3 (38%)' },
				]} />
			</div>
		</div>
	</PreviewShell>
));
DcerPreviewBody.displayName = 'DcerPreviewBody';

// ─── InsuranceCard ────────────────────────────────────────────────────────────
// Rows: checkbox (24px) + label (flex 1) + progress bar 65% + pct text.
// Progress: bg #EEF3F7, fill #92C7FF, h 10px. Selected: #4879F5 bg white text.
// Row height 32px. Scrollable list (fill full height).

export const InsurancePreviewBody = React.memo(({ useGenericLabels = false }) => (
	<PreviewShell>
		<div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', gap: 4 }}>
			{(useGenericLabels
				? [
					{ label: 'Test 1', pct: 62, count: 23, color: '#4879F5' },
					{ label: 'Test 2', pct: 21, count: 9, color: '#15BDB2' },
					{ label: 'Test 3', pct: 17, count: 1, color: '#4879F5' },
					{ label: 'Test 4', pct: 8, count: 1, color: '#FF3B30' },
					{ label: 'Test 5', pct: 5, count: 0, color: '#4879F5' },
					{ label: 'Test 6', pct: 4, count: 0, color: '#4879F5' },
					{ label: 'Test 7', pct: 2, count: 0, color: '#4879F5' },
					{ label: 'Test 8', pct: 1, count: 0, color: '#4879F5' },
				]
				: [
					{ label: 'Medicare', pct: 62, count: 23, color: '#4879F5' },
					{ label: 'Medicaid', pct: 21, count: 9, color: '#15BDB2' },
					{ label: 'Medicaid Pending', pct: 17, count: 1, color: '#4879F5' },
					{ label: 'Allstate Insurance', pct: 8, count: 1, color: '#FF3B30' },
					{ label: 'BCBS Comm', pct: 5, count: 0, color: '#4879F5' },
					{ label: 'Private Pay', pct: 4, count: 0, color: '#4879F5' },
					{ label: 'UHC Medicare', pct: 2, count: 0, color: '#4879F5' },
					{ label: 'BCBS Medicare', pct: 1, count: 0, color: '#4879F5' },
				]).map(({ label, pct, count, color }, i) => (
					<div key={label} style={{
						display: 'flex', alignItems: 'center',
						padding: '0 8px',
						flex: 1,
						minHeight: 34,
						gap: 12,
					}}>
						{/* Checkbox */}
						<div style={{ width: 14, height: 14, border: '1.5px solid #E2E8F0', borderRadius: 4, flexShrink: 0 }} />

						{/* Label */}
						<span style={{
							fontFamily: 'manrope', fontWeight: 600, fontSize: 13, color: '#444652',
							flexShrink: 0, width: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
						}}>{label}</span>

						{/* Indicator Bar */}
						<div style={{ width: 8, height: 20, backgroundColor: color, borderRadius: 4, flexShrink: 0 }} />

						{/* Value Pill */}
						<div style={{
							width: 72, height: 28, border: '1px solid #F1F4F9', borderRadius: 8,
							background: '#F8FAFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
							flexShrink: 0
						}}>
							<span style={{ fontFamily: 'manrope', fontSize: 11, fontWeight: 700, color: '#333' }}>
								{count} ({pct}%)
							</span>
						</div>

						{/* Progress bar */}
						<div style={{ flex: 1, height: 10, backgroundColor: '#EEF3F7', borderRadius: 10, overflow: 'hidden' }}>
							<div style={{ width: `${pct}%`, height: '100%', backgroundColor: '#92C7FF', borderRadius: 10 }} />
						</div>
					</div>
				))}
		</div>
	</PreviewShell>
));
InsurancePreviewBody.displayName = 'InsurancePreviewBody';

// ─── ReturnCardList ───────────────────────────────────────────────────────────
// Two item boxes: border #e8e8eb radius 8px h 45px, 50% width.
// labelCircle 13px circle — #2259C9 / #00BAEB.
// Pie chart: h 276px (dominant element), colors ["#2259C9","#00BAEB"].
// Donut at 230px fills the available 357px body (45px items + 16px gap + 276px chart).

export const ReturnsPreviewBody = React.memo(() => (
	<PreviewShell>
		<div style={{ width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
			{/* Two item boxes — 45px each */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexShrink: 0 }}>
				{[
					{ label: 'Returned', count: 4, pct: '40%', color: '#2259C9' },
					{ label: "Didn't Return", count: 6, pct: '60%', color: '#00BAEB' },
				].map(({ label, count, pct, color }) => (
					<div key={label} style={{
						flex: 1, height: 45, border: '1px solid #e8e8eb', borderRadius: 8,
						display: 'flex', alignItems: 'center', padding: '0 10px', gap: 8,
					}}>
						<div style={{ width: 13, height: 13, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
						<div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
							<span style={{ fontFamily: 'manrope', fontWeight: 700, fontSize: 11, color: '#444652', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
							<span style={{ fontFamily: 'manrope', fontSize: 10, color: '#636578' }}>{count} ({pct})</span>
						</div>
					</div>
				))}
			</div>
			{/* Pie chart — fills remaining height, matching real card's 276px chart */}
			<div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 0 }}>
				<PieChart size={230} segments={[
					{ pct: 40, color: '#2259C9', label: '4 (40%)' },
					{ pct: 60, color: '#00BAEB', label: '6 (60%)' },
				]} />
			</div>
		</div>
	</PreviewShell>
));
ReturnsPreviewBody.displayName = 'ReturnsPreviewBody';

// ─── NinetyDaysAnalysis ───────────────────────────────────────────────────────
// Left 63%: horizontal bar chart — CustomBar per time-range bucket.
//   Colors: #21B1D0, #5195DD, #4F80FF, #6B69DA, #1D7CD3.
// Right 37%: radial rings donut + 2-col legend.

const NINETY_ENTRIES = [
	{ label: '0-7 Days', color: '#21B1D0', pct: 32, h: 0.55 },
	{ label: '8-14 Days', color: '#5195DD', pct: 22, h: 0.35 },
	{ label: '15-30 Days', color: '#4F80FF', pct: 45, h: 0.70 },
	{ label: '31-60 Days', color: '#6B69DA', pct: 15, h: 0.45 },
	{ label: '61-90 Days', color: '#1D7CD3', pct: 28, h: 0.85 },
];

export const NinetyDaysPreviewBody = React.memo(() => (
	<PreviewShell>
		<div style={{ display: 'flex', width: '100%', height: '100%' }}>
			{/* Left 63% — horizontal bar chart */}
			<div style={{ width: '63%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10, paddingLeft: 2 }}>
				{NINETY_ENTRIES.map(({ label, color, pct }) => (
					<div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span style={{ fontFamily: 'manrope', fontSize: 10, fontWeight: 700, color: '#444652', width: 72, flexShrink: 0, lineHeight: 1.2 }}>{label}</span>
						<div style={{ flex: 1, height: 16, backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 25, overflow: 'hidden', position: 'relative' }}>
							<div style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: 25, opacity: 0.9 }} />
							<span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 9, fontWeight: 700, color: '#444652' }}>{pct}%</span>
						</div>
					</div>
				))}
			</div>
			{/* Right 37% — radial donut + legend */}
			<div style={{ width: '37%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
				<RadialBarChart size={100}
					entries={NINETY_ENTRIES.map(({ pct, color }) => ({ pct, color }))}
				/>
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 5, columnGap: 4 }}>
					{NINETY_ENTRIES.map(({ label, color }) => (
						<div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
							<div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
							<span style={{ fontSize: 8, color: '#636578', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{label}</span>
						</div>
					))}
				</div>
			</div>
		</div>
	</PreviewShell>
));
NinetyDaysPreviewBody.displayName = 'NinetyDaysPreviewBody';

// ─── FloorCardList ────────────────────────────────────────────────────────────
// Row: h 38px, bg #fff, box-shadow 0 1px 14px rgba(0,0,0,0.06), radius 8px, mb 10px.
// Left 56%: border 1px solid #e8e8e8 radius 8px, label manrope 500 15px #444652.
// Right 44%: ColorBox 9×18 + bold fs16 count.

export const FloorsPreviewBody = React.memo(() => (
	<PreviewShell>
		<div style={{ padding: '12px 8px 8px', width: '100%', height: '100%', boxSizing: 'border-box' }}>
			{[
				{ label: 'Unit 1', count: 12, compColor: '#4CAF50' },
				{ label: 'Unit 2', count: 8, compColor: '#4CAF50' },
				{ label: 'Unit 3', count: 5, compColor: '#F44336' },
				{ label: 'Unit 4', count: 3, compColor: '#4CAF50' },
				{ label: 'Unit 5', count: 7, compColor: '#4CAF50' },
				{ label: 'Unit 6', count: 2, compColor: '#F44336' },
			].map(({ label, count, compColor }) => (
				<div key={label} style={{
					display: 'flex', alignItems: 'center', height: 38,
					background: '#fff',
					boxShadow: '0px 1px 14px rgba(0,0,0,0.06)',
					borderRadius: 8, marginBottom: 10,
				}}>
					{/* Left: label box */}
					<div style={{
						width: '56%', height: '100%',
						display: 'flex', alignItems: 'center', paddingLeft: 8,
						border: '1px solid #e8e8e8', borderRadius: 8, background: '#fff',
						gap: 6,
					}}>
						<div style={{ width: 14, height: 14, border: '1px solid #c0c0c0', borderRadius: 3, flexShrink: 0 }} />
						<span style={{ fontFamily: 'manrope', fontWeight: 500, fontSize: 14, color: '#444652' }}>{label}</span>
					</div>
					{/* Right: comparison box + count */}
					<div style={{ width: '44%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
						<div style={{ width: 9, height: 18, backgroundColor: compColor, borderRadius: 2, flexShrink: 0 }} />
						<span style={{ fontFamily: 'manrope', fontWeight: 700, fontSize: 16, color: '#444652' }}>{count}</span>
					</div>
				</div>
			))}
		</div>
	</PreviewShell>
));
FloorsPreviewBody.displayName = 'FloorsPreviewBody';

// ─── UserList (doctor / nurse) ────────────────────────────────────────────────
// Row: h 52px. Primary: manrope 800 14px. Avatar 36px circle with initials.
// Secondary: ColorBox 9×18 + percentage text (12px) + checkbox.

const DOCTOR_ROWS = [
	{ initials: 'DA', name: 'Dr. Smith', pct: '10.9%', color: '#F44336' },
	{ initials: 'DC', name: 'Dr. Chandok', pct: '10.9%', color: '#F44336' },
	{ initials: 'SM', name: 'Dr. Martin', pct: '8.4%', color: '#F44336' },
	{ initials: 'RJ', name: 'Dr. Johnson', pct: '7.2%', color: '#4CAF50' },
	{ initials: 'MP', name: 'Dr. Patel', pct: '6.1%', color: '#4CAF50' },
];

const NURSE_ROWS = [
	{ initials: 'WN', name: 'Nurse Woon, Nyree', pct: '10.9%', color: '#F44336' },
	{ initials: 'RC', name: 'Nurse Romualdo, Cynthia', pct: '10.9%', color: '#F44336' },
	{ initials: 'CE', name: 'Nurse Chilcott, Elizabeth', pct: '8.4%', color: '#F44336' },
	{ initials: 'JR', name: 'Nurse Johashen, Roberta', pct: '7.2%', color: '#4CAF50' },
	{ initials: 'OE', name: 'Nurse Osahon, Esogie', pct: '6.1%', color: '#4CAF50' },
];

const GENERIC_TEST_ROWS = [
	{ initials: 'T1', name: 'Test 1', pct: '10.9%', color: '#F44336' },
	{ initials: 'T2', name: 'Test 2', pct: '10.9%', color: '#F44336' },
	{ initials: 'T3', name: 'Test 3', pct: '8.4%', color: '#F44336' },
	{ initials: 'T4', name: 'Test 4', pct: '7.2%', color: '#4CAF50' },
	{ initials: 'T5', name: 'Test 5', pct: '6.1%', color: '#4CAF50' },
];

export const UserListPreviewBody = React.memo(({ role = 'generic' }) => (
	<PreviewShell>
		<div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
			{(role === 'doctor' ? DOCTOR_ROWS : role === 'nurse' ? NURSE_ROWS : GENERIC_TEST_ROWS).map(({ initials, name, pct, color }, i) => (
				<div key={name}>
					<div style={{
						display: 'flex', alignItems: 'center', height: 52,
						paddingLeft: 8, paddingRight: 8,
					}}>
						{/* Avatar */}
						<div style={{
							width: 36, height: 36, borderRadius: '50%',
							backgroundColor: 'rgba(0,0,0,0.08)',
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							fontFamily: 'manrope', fontSize: 11, fontWeight: 700, color: '#444652',
							flexShrink: 0, marginRight: 10,
						}}>{initials}</div>
						{/* Name */}
						<span style={{ fontFamily: 'manrope', fontWeight: 800, fontSize: 13, color: '#444652', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
						{/* Comparison + pct */}
						<div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 8 }}>
							<div style={{ width: 9, height: 18, backgroundColor: color, borderRadius: 2, flexShrink: 0 }} />
							<span style={{ fontFamily: 'manrope', fontSize: 12, color: '#444652' }}>({pct})</span>
						</div>
						<div style={{ width: 16, height: 16, border: '1px solid #c0c0c0', borderRadius: 3 }} />
					</div>
					{i < 4 && <div style={{ height: 1, backgroundColor: 'rgba(0,0,0,0.05)', margin: '0 8px' }} />}
				</div>
			))}
		</div>
	</PreviewShell>
));
UserListPreviewBody.displayName = 'UserListPreviewBody';

// ─── DayCardList ──────────────────────────────────────────────────────────────
// Nivo ResponsiveBar: bars #FFECA6 (light yellow), selected bar #FFC700 (gold).
// h 200–245px. Checkbox footer below bars with day label.

const DAY_DATA = [
	{ label: 'Sun', count: 1, pct: 10, color: '#0047FF' },
	{ label: 'Mon', count: 2, pct: 20, color: '#82C91E' },
	{ label: 'Tue', count: 2, pct: 20, color: '#82C91E' },
	{ label: 'Wed', count: 1, pct: 10, color: '#0047FF' },
	{ label: 'Thu', count: 2, pct: 20, color: '#0047FF' },
	{ label: 'Fri', count: 1, pct: 10, color: '#0047FF' },
	{ label: 'Sat', count: 1, pct: 10, color: '#00C2FF' },
];

export const DaysPreviewBody = React.memo(() => (
	<PreviewShell>
		<div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', padding: '10px 4px 6px' }}>
			{/* Bar chart Section */}
			<div style={{
				display: 'flex', alignItems: 'flex-end', gap: 6,
				flex: 1, padding: '0 8px', boxSizing: 'border-box',
				minHeight: 0
			}}>
				{DAY_DATA.map((d, i) => (
					<div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', position: 'relative' }}>
						<div style={{
							width: '100%', height: `${Math.max(30, d.pct * 4)}%`,
							backgroundColor: '#FFECB3',
							borderRadius: 6,
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							transition: 'height 0.3s ease'
						}}>
							<span style={{ fontSize: 10, fontWeight: 800, color: '#333', fontFamily: 'manrope' }}>{d.pct}%</span>
						</div>
					</div>
				))}
			</div>

			{/* Day Labels */}
			<div style={{ display: 'flex', justifyContent: 'space-evenly', padding: '10px 8px 6px' }}>
				{DAY_DATA.map((d, i) => (
					<span key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: '#444652', fontWeight: 600, fontFamily: 'manrope' }}>{d.label}</span>
				))}
			</div>

			{/* Control Pills: Count + Checkbox */}
			<div style={{ display: 'flex', justifyContent: 'space-evenly', gap: 6, padding: '0 8px' }}>
				{DAY_DATA.map((d, i) => (
					<div key={i} style={{ 
						flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, 
						backgroundColor: '#F8F9FB', borderRadius: 8, padding: '6px 0', border: '1px solid #F1F4F9'
					}}>
						<span style={{ fontSize: 11, fontWeight: 700, color: '#333' }}>{d.count}</span>
						<div style={{ width: 12, height: 12, border: '1px solid #CBD5E1', borderRadius: 3, backgroundColor: '#fff' }} />
					</div>
				))}
			</div>

			{/* Bottom Indicator Tabs */}
			<div style={{ display: 'flex', justifyContent: 'space-evenly', padding: '8px 8px 0' }}>
				{DAY_DATA.map((d, i) => (
					<div key={i} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
						<div style={{ width: 6, height: 14, backgroundColor: d.color, borderRadius: 3 }} />
					</div>
				))}
			</div>
		</div>
	</PreviewShell>
));
DaysPreviewBody.displayName = 'DaysPreviewBody';

// ─── DiagnosticsCardList ───────────────────────────────────────────────────────
// Row: box-shadow 0 0 10px rgba(0,0,0,0.14) radius 8px p 5px.
// Title: manrope 700 15px #444652. Count box: 80×38 border #e8e8eb radius 8px.
// ColorBox 9×18 on left.

export const DxPreviewBody = React.memo(({ useGenericLabels = false }) => (
	<PreviewShell>
		<div style={{ padding: '4px 0', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 8, boxSizing: 'border-box' }}>
			{(useGenericLabels
				? [
					{ label: 'Test 1', count: '6 (25%)', color: '#4CAF50' },
					{ label: 'Test 2', count: '4 (17%)', color: '#F44336' },
					{ label: 'Test 3', count: '3 (13%)', color: '#4CAF50' },
					{ label: 'Test 4', count: '2 (8%)', color: '#F44336' },
					{ label: 'Test 5', count: '2 (8%)', color: '#F44336' },
					{ label: 'Test 6', count: '2 (8%)', color: '#4CAF50' },
				]
				: [
					{ label: 'Diagnosis UTI', count: '6 (25%)', color: '#4CAF50' },
					{ label: 'Diagnosis Pneumonia', count: '4 (17%)', color: '#F44336' },
					{ label: 'Diagnosis CHF', count: '3 (13%)', color: '#4CAF50' },
					{ label: 'Diagnosis COPD', count: '2 (8%)', color: '#F44336' },
					{ label: 'Diagnosis Sepsis', count: '2 (8%)', color: '#F44336' },
					{ label: 'Diagnosis Cellulitis', count: '2 (8%)', color: '#4CAF50' },
				]).map(({ label, count, color }) => (
					<div key={label} style={{
						display: 'flex', alignItems: 'center',
						background: '#fff',
						boxShadow: '0px 0px 10px rgba(0,0,0,0.14)',
						borderRadius: 8, padding: '0 8px',
						flex: 1, minHeight: 34,
					}}>
						<div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: 6 }}>
							<div style={{ width: 9, height: 18, backgroundColor: color, borderRadius: 2, flexShrink: 0 }} />
							<div style={{ width: 14, height: 14, border: '1px solid #c0c0c0', borderRadius: 3, flexShrink: 0 }} />
							<span style={{ fontFamily: 'manrope', fontWeight: 700, fontSize: 13, color: '#444652' }}>{label}</span>
						</div>
						<div style={{
							width: 72, height: 30, border: '1px solid #e8e8eb', borderRadius: 8,
							display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
						}}>
							<span style={{ fontFamily: 'manrope', fontSize: 11, fontWeight: 700, color: '#444652' }}>{count}</span>
						</div>
					</div>
				))}
		</div>
	</PreviewShell>
));
DxPreviewBody.displayName = 'DxPreviewBody';

// ─── ShiftCard ────────────────────────────────────────────────────────────────
// Bar color default #636578, selected #4879F5. barWrpr min-height 230px.
// Label button: border #636578 radius 8px h 33px, manrope 14px #444652.
// Number + comparison box above bar.

export const ShiftPreviewBody = React.memo(({ useGenericLabels = false }) => (
	<PreviewShell>
		<div style={{ display: 'flex', justifyContent: 'center', gap: 20, padding: '0 12px', alignItems: 'flex-end', width: '100%', height: '100%', paddingBottom: 16 }}>
			{(useGenericLabels
				? [
					{ label: 'Test 1', value: 5, pct: 50 },
					{ label: 'Test 2', value: 3, pct: 30 },
					{ label: 'Test 3', value: 2, pct: 20 },
				]
				: [
					{ label: 'Morning', value: 5, pct: 50 },
					{ label: 'Evening', value: 3, pct: 30 },
					{ label: 'Night', value: 2, pct: 20 },
				]).map(({ label, value, pct }) => (
				<div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, height: '100%' }}>
					{/* Spacer to push everything to the bottom */}
					<div style={{ flex: 1 }} />

					{/* Main "Black" (Dark Gray) Chart Bar */}
					<div style={{
						width: '80%', height: value * 24, backgroundColor: '#636578',
						borderRadius: 6, marginBottom: 10, opacity: 0.9
					}} />

					{/* Number + Blue Indicator (Small/Fixed) above baseline */}
					<div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
						<div style={{ width: 6, height: 18, backgroundColor: '#0047FF', borderRadius: 3 }} />
						<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
							<span style={{ fontFamily: 'manrope', fontWeight: 700, fontSize: 16, color: '#333' }}>{value}</span>
							<span style={{ fontFamily: 'manrope', fontSize: 10, fontWeight: 600, color: '#64748B' }}>({pct}%)</span>
						</div>
					</div>

					{/* Horizontal Baseline */}
					<div style={{ width: '100%', height: 2, backgroundColor: '#E2E8F0', borderRadius: 1, marginBottom: 12 }} />

					{/* Pill Button Label */}
					<div style={{
						width: '100%', height: 36, border: '1px solid #E2E8F0',
						borderRadius: 10, display: 'flex', alignItems: 'center', padding: '0 10px', gap: 8,
						backgroundColor: '#fff', boxSizing: 'border-box'
					}}>
						<div style={{ width: 14, height: 14, border: '1.5px solid #CBD5E1', borderRadius: 4, flexShrink: 0 }} />
						<span style={{ fontFamily: 'manrope', fontSize: 12, fontWeight: 600, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
					</div>
				</div>
			))}
		</div>
	</PreviewShell>
));
ShiftPreviewBody.displayName = 'ShiftPreviewBody';

// ─── HospitalSlider ───────────────────────────────────────────────────────────
// Cards: bg #fff, box-shadow 0 0 10px rgba(0,0,0,0.14), radius 8px, p 15px 18px.
// labelCircle 13px: Transferred #15BDB2, DC #076673. 3 slides visible. Dots below.

export const SliderPreviewBody = React.memo(() => (
	<PreviewShell>
		<div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', position: 'relative', justifyContent: 'center' }}>
			{/* Navigation Arrows — adjusted positioning for shorter cards */}
			<div style={{
				position: 'absolute', left: -10, top: '50%', transform: 'translateY(-50%)',
				width: 28, height: 28, borderRadius: '50%', backgroundColor: '#fff',
				border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center',
				boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10, cursor: 'pointer'
			}}>
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#636578" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
			</div>
			<div style={{
				position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)',
				width: 28, height: 28, borderRadius: '50%', backgroundColor: '#fff',
				border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center',
				boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10, cursor: 'pointer'
			}}>
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#636578" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
			</div>

			{/* Slide cards — wider and shorter as requested */}
			<div style={{ display: 'flex', gap: 16, overflow: 'hidden', padding: '10px 4px 10px' }}>
				{[
					{ name: 'Glen Cove Hospital', trans: 26, transPct: 29, dc: 20, dcPct: 77, gaugePct: 82 },
					{ name: 'North Shore University Hospital', trans: 4, transPct: 4, dc: 4, dcPct: 100, gaugePct: 100 },
					{ name: 'Manhasset Hospital', trans: 2, transPct: 2, dc: 2, dcPct: 100, gaugePct: 92 },
					{ name: 'Nassau University Medical Center', trans: 1, transPct: 1, dc: 1, dcPct: 100, gaugePct: 100 },
					{ name: 'St. Francis Hospital', trans: 3, transPct: 2, dc: 2, dcPct: 95, gaugePct: 95 },
					{ name: 'Long Island Jewish Medical Center', trans: 5, transPct: 4, dc: 4, dcPct: 100, gaugePct: 100 },
					{ name: 'Huntington Hospital', trans: 2, transPct: 2, dc: 2, dcPct: 100, gaugePct: 90 },
					{ name: 'St. Catherine of Siena', trans: 1, transPct: 1, dc: 1, dcPct: 100, gaugePct: 100 },
				].map((h) => (
					<div key={h.name} style={{
						flex: '0 0 280px',
						height: 140,
						background: '#fff',
						border: '1px solid #F1F4F9',
						borderRadius: 12,
						padding: '16px 18px',
						display: 'flex', flexDirection: 'column',
						minWidth: 0, boxSizing: 'border-box',
						position: 'relative',
						boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
					}}>
						{/* Card Title */}
						<span style={{
							fontFamily: 'manrope', fontWeight: 700, fontSize: 13, color: '#334155',
							overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
							marginBottom: 'auto', paddingRight: 20
						}}>{h.name}</span>

						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							{/* Stats column */}
							<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
								{[
									{ label: 'Transferred', color: '#15BDB2', val: h.trans, pct: h.transPct },
									{ label: 'DC', color: '#076673', val: h.dc, pct: h.dcPct },
								].map(s => (
									<div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
										<div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }} />
										<div style={{ width: 4, height: 16, backgroundColor: '#FF3B30', borderRadius: 2, flexShrink: 0 }} />
										<span style={{ fontFamily: 'manrope', fontWeight: 600, fontSize: 11, color: '#444652', whiteSpace: 'nowrap' }}>
											{s.label}: {s.val} <span style={{ color: '#64748B', fontWeight: 500 }}>({s.pct}%)</span>
										</span>
									</div>
								))}
							</div>
							{/* Circular Gauge */}
							<div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
								<svg width="52" height="52" style={{ transform: 'rotate(-90deg)' }}>
									<circle cx="26" cy="26" r="22" fill="none" stroke="#F1F5F9" strokeWidth="4" />
									<circle cx="26" cy="26" r="22" fill="none" stroke="#15BDB2" strokeWidth="5"
										strokeDasharray={`${(h.gaugePct / 100) * 138} 138`} strokeLinecap="round" />
									<circle cx="26" cy="26" r="14" fill="none" stroke="#F1F5F9" strokeWidth="4" />
									<circle cx="26" cy="26" r="14" fill="none" stroke="#15BDB2" strokeWidth="5"
										strokeDasharray={`${(Math.max(20, h.gaugePct - 30) / 100) * 88} 88`} strokeLinecap="round" />
								</svg>
							</div>
						</div>

						{/* Checkbox top-right */}
						<div style={{ position: 'absolute', top: 16, right: 16, width: 14, height: 14, border: '1.5px solid #CBD5E1', borderRadius: 4, backgroundColor: '#fff' }} />
					</div>
				))}
			</div>

			{/* Centered Pagination Dots */}
			<div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 10, flexShrink: 0 }}>
				{[0, 1, 2, 3].map((i) => (
					<div key={i} style={{
						width: 7, height: 7, borderRadius: '50%',
						backgroundColor: i === 0 ? '#475569' : '#CBD5E1',
					}} />
				))}
			</div>
		</div>
	</PreviewShell>
));
SliderPreviewBody.displayName = 'SliderPreviewBody';
SliderPreviewBody.displayName = 'SliderPreviewBody';

// ─── CombineTotal (dynamic card) ──────────────────────────────────────────────
// Big total (left half) + vertical divider + sub-total rows (right half).

export const CombineTotalPreviewBody = React.memo(({ useGenericLabels = false }) => (
	<PreviewShell>
		<div style={{
			flex: 1, minHeight: 0,
			display: 'flex', alignItems: 'stretch',
			height: '100%',
			boxSizing: 'border-box',
		}}>
			{/* Left Side: Big total */}
			<div style={{
				flex: '0 0 50%',
				display: 'flex', flexDirection: 'column',
				justifyContent: 'center', alignItems: 'center',
				position: 'relative'
			}}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
					{/* Status Bar */}
					<div style={{ width: 8, height: 24, backgroundColor: '#4879F5', borderRadius: 4 }} />
					<span style={{ fontFamily: 'manrope', fontSize: 64, fontWeight: 700, color: '#4879F5', lineHeight: 1 }}>8</span>
				</div>
				<span style={{ fontFamily: 'manrope', fontSize: 20, fontWeight: 600, color: '#4879F5', marginTop: 8 }}>(37%)</span>
			</div>

			{/* Divider */}
			<div style={{ flex: '0 0 1px', backgroundColor: '#F1F4F9', alignSelf: 'stretch', margin: '15px 0' }} />

			{/* Right Side: Status Cards */}
			<div style={{
				flex: 1, display: 'flex', flexDirection: 'column',
				justifyContent: 'center', gap: 12, padding: '0 12px'
			}}>
				{(useGenericLabels
					? [
						{ label: 'Test 1', count: 4, pct: '12%', color: '#4879F5' },
						{ label: 'Test 2', count: 2, pct: '10%', color: '#4879F5' },
						{ label: 'Test 3', count: 2, pct: '15%', color: '#82C91E' },
					]
					: [
						{ label: 'Test 1', count: 4, pct: '12%', color: '#4879F5' },
						{ label: 'Test 2', count: 2, pct: '10%', color: '#4879F5' },
						{ label: 'Test 3', count: 2, pct: '15%', color: '#82C91E' },
					]).map(({ label, count, pct, color }) => (
						<div key={label} style={{
							display: 'flex', alignItems: 'center', gap: 10,
							border: '1px solid #4879F5', borderRadius: 10, padding: '10px 12px',
							position: 'relative', background: '#F8FAFF', minHeight: 64,
							width: '85%', alignSelf: 'center'
						}}>
							{/* Current Badge */}
							<div style={{
								position: 'absolute', top: -10, right: 10,
								backgroundColor: '#1E88E5', color: '#fff', fontSize: 9, fontWeight: 800,
								padding: '2px 8px', borderRadius: 20, letterSpacing: '0.5px',
								boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
							}}>CURRENT</div>

							{/* Indicator Bar */}
							<div style={{ width: 8, height: 24, backgroundColor: color, borderRadius: 4, flexShrink: 0 }} />

							<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: 2 }}>
								<span style={{ fontFamily: 'manrope', fontSize: 13, fontWeight: 600, color: '#4879F5', textAlign: 'center' }}>{label}</span>
								<span style={{ fontFamily: 'manrope', fontSize: 18, fontWeight: 700, color: '#333' }}>{count}</span>
								<span style={{ fontFamily: 'manrope', fontSize: 12, fontWeight: 600, color: '#4879F5' }}>({pct})</span>
							</div>
						</div>
					))}
			</div>
		</div>
	</PreviewShell>
));
CombineTotalPreviewBody.displayName = 'CombineTotalPreviewBody';
